import React from 'react';
import {
  Action, ActionIcon, Fieldset, FormHandlers, ICON_CLOSE, ICON_MAGNIFY_PLUS_OUTLINE, ICON_PENCIL_OUTLINE, Message, Portal, Spacing, Stack, useModal, useNotifications,
} from '@doveit/bricks';
import { isAxiosError } from 'axios';
import { Company } from '../../../providers/api/dtos';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { getCompany } from '../../../providers/api/company/companyProvider';
import CompanyForm, { CompanyFormModel } from '../../components/company-form/CompanyForm';
import { companyToCompanyFormModal } from '../../components/company-form/mappers';
import ViewCompanyPreview from '../view-company-preview/ViewCompanyPreview';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import { geoFormModelToGeo } from '../../../geo/components/form-geo-field/mappers';

interface NoCompanySelectedProps {
  openSearchModal: VoidFunction,
  isSearching: boolean,
}

interface CompanySelectedProps {
  company?: Company,
  searchValues?: CompanyFormModel,
  clearCompany: VoidFunction,
  updateCompany: VoidFunction,
}

export interface SelectCompanyProps extends React.AriaAttributes {
  company?: Company,
  onChange?: (existingCompany?: Company, formValues?: CompanyFormModel) => void,
  canChange?: boolean,
  render?: {
    NoCompanySelected?: (props: NoCompanySelectedProps) => React.ReactNode,
    CompanySelected?: (props: CompanySelectedProps) => React.ReactNode,
  }
}

export const GET_COMPANY_BY_VAT_NUMBER_ERROR_MESSAGE = 'Non è stato possibile verificare l\'esistenza della persona giuridica';
export const NEW_COMPANY_WARNING_MESSAGE = 'Nessuna persona giuridica trovata. Verrà creata una nuova persona giuridica con i seguenti dati.';

const SelectCompany: React.FC<SelectCompanyProps> = ({
  onChange,
  canChange = true,
  company: initialCompany,
  render,
}) => {
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchValues, setSearchValues] = React.useState<CompanyFormModel>();
  const [company, setCompany] = React.useState(initialCompany);
  const [isSelected, setIsSelected] = React.useState(false);

  const { addError } = useNotifications();

  const searchCompanyModal = useModal();
  const searchCompanyFormRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const createCompanyModal = useModal<Partial<CompanyFormModel>>();
  const createCompanyFormRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const updateCompanyModal = useModal<Company>();
  const updateCompanyFormRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  React.useEffect(() => {
    if (initialCompany) {
      setCompany(initialCompany);
      setIsSelected(true);
    }
  }, [initialCompany]);

  const handleCompanyChange = React.useCallback((comp?: Company, overwriteValues?: CompanyFormModel) => {
    setCompany(comp);
    setSearchValues(overwriteValues);
    setIsSelected(comp !== undefined || overwriteValues !== undefined);
    onChange?.(comp, overwriteValues);
  }, [onChange]);

  const searchAndMaybeCreateCompany = React.useCallback(async ({ vatNumber }: CompanyFormModel) => {
    try {
      setIsSearching(true);

      const existingCompany = await getCompany(vatNumber);

      handleCompanyChange(existingCompany);
      setIsSearching(false);
      searchCompanyModal.close();
    } catch (err) {
      setIsSearching(false);

      if (isAxiosError(err) && err.response?.status === 404) {
        searchCompanyModal.close();
        createCompanyModal.open({
          vatNumber,
        });
        return;
      }

      addError(GET_COMPANY_BY_VAT_NUMBER_ERROR_MESSAGE);
    }
  }, [addError, createCompanyModal, handleCompanyChange, searchCompanyModal]);

  const createCompany = React.useCallback((formModel: CompanyFormModel) => {
    handleCompanyChange(undefined, formModel);

    createCompanyModal.close();
  }, [createCompanyModal, handleCompanyChange]);

  const updateCompany = React.useCallback((formModel: CompanyFormModel) => {
    handleCompanyChange(company, formModel);

    updateCompanyModal.close();
  }, [company, handleCompanyChange, updateCompanyModal]);

  const viewCompany = React.useMemo<Company>(() => ({
    ...(company ?? {}),
    address: (searchValues?.address ? geoFormModelToGeo(searchValues.address) : company?.address!) || {},
    vatNumber: searchValues?.vatNumber || company?.vatNumber || '',
    name: searchValues?.name || company?.name || '',
  }), [company, searchValues?.address, searchValues?.name, searchValues?.vatNumber]);

  return (
    <>
      {!isSelected && (render?.NoCompanySelected
        ? render.NoCompanySelected({
          isSearching,
          openSearchModal: searchCompanyModal.open,
        })
        : (
          <Action
            aria-label="Azione per cercare o aggiungere una persona giuridica"
            label="Cerca o aggiungi persona giuridica"
            iconLeft={{ path: ICON_MAGNIFY_PLUS_OUTLINE }}
            size="M"
            onClick={() => searchCompanyModal.open()}
            loading={isSearching}
          />
        ))}

      {isSelected && !company && (
        <Spacing margin={[100, 0]}>
          <Message
            type="warning"
            message={NEW_COMPANY_WARNING_MESSAGE}
          />
        </Spacing>
      )}

      {isSelected && (render?.CompanySelected
        ? render.CompanySelected({
          company,
          searchValues,
          clearCompany: () => handleCompanyChange(),
          updateCompany: () => updateCompanyModal.open(company),
        })
        : (
          <ViewCompanyPreview
            company={viewCompany}
            primaryActions={removeNullAndUndefinedValues([
              canChange ? (
                <ActionIcon
                  label="Modifica persona giuridica"
                  size="S"
                  aria-label="Modifica persona giuridica"
                  onClick={() => updateCompanyModal.open(company)}
                  icon={{ path: ICON_PENCIL_OUTLINE }}
                />
              ) : undefined,
              canChange ? (
                <ActionIcon
                  label="Deseleziona persona giuridica"
                  size="S"
                  aria-label="Deseleziona persona giuridica"
                  onClick={() => handleCompanyChange()}
                  icon={{ path: ICON_CLOSE }}
                />
              ) : undefined,
            ])}
          />
        ))}

      <Portal>
        <SimpleModal
          {...searchCompanyModal}
          title="Aggiungi persona giuridica"
          aria-label="Modale per cercare ed eventualmente aggiungere la persona giuridica"
          footer={(
            <Action
              label="Aggiungi"
              color="primary"
              emphasis="high"
              aria-label="Pulsante per cercare ed eventualmente aggiungere la persona giuridica"
              onClick={() => searchCompanyFormRef.current.handleSubmit()}
            />
          )}
        >
          <CompanyForm
            innerRef={searchCompanyFormRef}
            hiddenFields={{
              address: true,
              name: true,
            }}
            onSubmit={searchAndMaybeCreateCompany}
            preventDuplicateVatNumber={false}
          />
        </SimpleModal>

        <SimpleModal
          {...createCompanyModal}
          title="Aggiungi persona giuridica"
          aria-label="Modale per aggiungere la persona giuridica"
          footer={(
            <Action
              label="Aggiungi"
              color="primary"
              emphasis="high"
              aria-label="Pulsante per aggiungere la persona giuridica"
              onClick={() => createCompanyFormRef.current.handleSubmit()}
            />
          )}
        >
          <Stack gap={100}>
            <Message
              type="warning"
              message={`Nessuna persona giuridica con P. IVA ${createCompanyModal.data?.vatNumber}`}
            />

            <Spacing margin={[200, 0, 0]}>
              <Fieldset legend="Aggiungi persona giuridica" />
            </Spacing>

            <CompanyForm
              innerRef={createCompanyFormRef}
              initialValues={createCompanyModal.data}
              onSubmit={createCompany}
              preventDuplicateVatNumber
            />
          </Stack>
        </SimpleModal>

        {updateCompanyModal.data && (
          <SimpleModal
            {...updateCompanyModal}
            title="Modifica persona giuridica"
            aria-label="Modale per modificare la persona giuridica"
            footer={(
              <Action
                label="Modifica"
                color="primary"
                emphasis="high"
                aria-label="Pulsante per modificare la persona giuridica"
                onClick={() => updateCompanyFormRef.current.handleSubmit()}
              />
            )}
          >
            <CompanyForm
              innerRef={updateCompanyFormRef}
              initialValues={companyToCompanyFormModal(updateCompanyModal.data)}
              onSubmit={updateCompany}
              preventDuplicateVatNumber
              excludedVatNumbers={[updateCompanyModal.data.vatNumber]}
            />
          </SimpleModal>
        )}
      </Portal>
    </>
  );
};

export default SelectCompany;
